import { DropdownMenu } from "@/components/DropdownMenu";
import { MoreMenuButton } from "@/components/MoreMenuButton";
import { CO_SIGNING_PAUSE_MESSAGE } from "@/components/PausedActionTooltip";
import { Table } from "@/components/Table";

import { userRoleText } from "@/helpers/userRoleText";

import {
  canDeactivateUser,
  canInviteCoSigner,
  canRemoveCoSigner,
  getApprovedCoSignerCount,
  getCoSignerRowInfo,
  isEligibleForCoSigning,
} from "../utils/coSignerLookup";

import { CoSignerCell } from "./CoSignerCell";

import { ApiUser, CoSigningStatus } from "@/types";

import "./CoSignerSigningModal.scss";
interface MembersTableProps {
  users: ApiUser[];
  coSigningStatus: CoSigningStatus | undefined;
  /** ID of the currently signed-in viewer. Used to render the "(you)" marker. */
  currentUserId?: string;
  /**
   * Whether the viewer can manage members. When `false` (e.g. Financial
   * Controller), the kebab column is hidden, the Invited pill is not
   * clickable, and the Pending-approval row hides the Review button.
   */
  canManage: boolean;
  /** Approved-count denominator surfaced in the "Co-signer (n/max)" header. */
  maxCoSigners: number;
  /** Owner clicked "Change role" in a member's kebab. */
  onChangeRoleRequest: (user: ApiUser) => void;
  onToggleStatusRequest: (user: ApiUser) => void;
  onSimulateInvitedToPending: (userId: string) => void;
  onReviewCoSignerRequest?: (user: ApiUser) => void;
  onRemoveCoSignerRequest?: (user: ApiUser) => void;
  onInviteCoSignerRequest?: (user: ApiUser) => void;
  onRevokeInvitationRequest?: (user: ApiUser) => void;
  /**
   * Owner clicked "Reactivate" for a deactivated user. Called with the row
   * user; the handler flips `is_active` back to `true` immediately without
   * opening the ToggleStatusModal (matches the standalone prototype).
   */
  onReactivateRequest?: (user: ApiUser) => void;
  /**
   * A turn-off request is pending (the co-signing pause). Co-signer
   * management is frozen: Review is disabled and the invite / remove /
   * revoke menu items are locked with the pause message. Change role and
   * Deactivate stay available.
   */
  isPaused?: boolean;
}

export const MembersTable = ({
  users,
  coSigningStatus,
  currentUserId,
  canManage,
  maxCoSigners,
  onChangeRoleRequest,
  onToggleStatusRequest,
  onSimulateInvitedToPending,
  onReviewCoSignerRequest,
  onRemoveCoSignerRequest,
  onInviteCoSignerRequest,
  onRevokeInvitationRequest,
  onReactivateRequest,
  isPaused = false,
}: MembersTableProps) => {
  const approvedCount = getApprovedCoSignerCount(coSigningStatus);

  const renderPausedItem = (key: string, label: string) => (
    <div key={key} className="MembersTable__disabledItem">
      <span className="MembersTable__disabledItemLabel">{label}</span>
      <span className="MembersTable__disabledItemReason">{CO_SIGNING_PAUSE_MESSAGE}</span>
    </div>
  );

  const renderMemberName = (user: ApiUser) => {
    const base =
      user.first_name || user.last_name ? `${user.first_name} ${user.last_name}` : user.email;
    const isViewer = currentUserId && user.id === currentUserId;

    return (
      <span className="MembersTable__memberName">
        {base}
        {isViewer ? <span className="MembersTable__youMarker"> (you)</span> : null}
      </span>
    );
  };

  const renderCoSigner = (user: ApiUser) => {
    const info = getCoSignerRowInfo(user.id, coSigningStatus);
    return (
      <CoSignerCell
        info={info}
        onInvitedClick={
          canManage && info?.state === "invited"
            ? () => onSimulateInvitedToPending(user.id)
            : undefined
        }
        onReviewClick={
          canManage && info?.state === "pending" && onReviewCoSignerRequest
            ? () => onReviewCoSignerRequest(user)
            : undefined
        }
        isPaused={isPaused}
      />
    );
  };

  const renderCoSignerMenuItems = (user: ApiUser) => {
    const info = getCoSignerRowInfo(user.id, coSigningStatus);
    const primaryRole = user.roles?.[0];

    if (!info) {
      if (!isEligibleForCoSigning(primaryRole) || !onInviteCoSignerRequest) return null;
      if (isPaused) return renderPausedItem(`${user.id}-invite-paused`, "Invite as co-signer");

      const guard = canInviteCoSigner(user.id, coSigningStatus);
      if (guard.ok) {
        return (
          <DropdownMenu.Item
            key={`${user.id}-invite`}
            onClick={() => onInviteCoSignerRequest(user)}
          >
            Invite as co-signer
          </DropdownMenu.Item>
        );
      }
      return (
        <div key={`${user.id}-invite-disabled`} className="MembersTable__disabledItem">
          <span className="MembersTable__disabledItemLabel">Invite as co-signer</span>
          <span className="MembersTable__disabledItemReason">{guard.reason}</span>
        </div>
      );
    }

    if (info.state === "pending" && onReviewCoSignerRequest) {
      if (isPaused) return renderPausedItem(`${user.id}-review-paused`, "Review co-signer");
      return (
        <DropdownMenu.Item key={`${user.id}-review`} onClick={() => onReviewCoSignerRequest(user)}>
          Review co-signer
        </DropdownMenu.Item>
      );
    }

    if (info.state === "invited" && onRevokeInvitationRequest) {
      if (isPaused) {
        return renderPausedItem(`${user.id}-revoke-paused`, "Revoke co-signer invitation");
      }
      return (
        <DropdownMenu.Item
          key={`${user.id}-revoke`}
          onClick={() => onRevokeInvitationRequest(user)}
        >
          Revoke co-signer invitation
        </DropdownMenu.Item>
      );
    }

    if (info.state === "approved" && onRemoveCoSignerRequest) {
      if (isPaused) return renderPausedItem(`${user.id}-remove-paused`, "Remove as co-signer");
      const guard = canRemoveCoSigner(user.id, coSigningStatus, users);
      if (guard.ok) {
        return (
          <DropdownMenu.Item
            key={`${user.id}-remove`}
            onClick={() => onRemoveCoSignerRequest(user)}
          >
            Remove as co-signer
          </DropdownMenu.Item>
        );
      }
      return (
        <div key={`${user.id}-remove-disabled`} className="MembersTable__disabledItem">
          <span className="MembersTable__disabledItemLabel">Remove as co-signer</span>
          <span className="MembersTable__disabledItemReason">{guard.reason}</span>
        </div>
      );
    }

    return null;
  };

  const renderChangeRoleMenuItems = (user: ApiUser) => {
    const info = getCoSignerRowInfo(user.id, coSigningStatus);

    // Not a co-signer, or only Invited (pre-key-setup) — role changes are safe.
    // Approved & Pending-approval co-signers block role changes for phase 1
    // to avoid key-weight / capability drift while a slot is held.
    const isBlocked = info?.state === "approved" || info?.state === "pending";

    if (!isBlocked) {
      return (
        <DropdownMenu.Item key={`${user.id}-change-role`} onClick={() => onChangeRoleRequest(user)}>
          Change role
        </DropdownMenu.Item>
      );
    }

    return (
      <div key={`${user.id}-change-role-disabled`} className="MembersTable__disabledItem">
        <span className="MembersTable__disabledItemLabel">Change role</span>
        <span className="MembersTable__disabledItemReason">
          Co-signers can&apos;t change roles. Remove them as a co-signer first.
        </span>
      </div>
    );
  };

  const renderStatusMenuItem = (user: ApiUser) => {
    // Reactivate: immediate, no modal, no Owner-floor guard (reactivating
    // never violates it — it only adds active members back).
    if (!user.is_active) {
      return (
        <DropdownMenu.Item
          key={`${user.id}-reactivate`}
          onClick={() =>
            onReactivateRequest ? onReactivateRequest(user) : onToggleStatusRequest(user)
          }
          isHighlight
        >
          Reactivate
        </DropdownMenu.Item>
      );
    }

    // Deactivate: subject to the two-active-Owner floor for Owner co-signers.
    const guard = canDeactivateUser(user.id, coSigningStatus, users);
    if (guard.ok) {
      return (
        <DropdownMenu.Item
          key={`${user.id}-deactivate`}
          onClick={() => onToggleStatusRequest(user)}
          isHighlight
        >
          Deactivate
        </DropdownMenu.Item>
      );
    }
    return (
      <div key={`${user.id}-deactivate-disabled`} className="MembersTable__disabledItem">
        <span className="MembersTable__disabledItemLabel">Deactivate</span>
        <span className="MembersTable__disabledItemReason">{guard.reason}</span>
      </div>
    );
  };

  const displayName = (user: ApiUser) =>
    [user.first_name, user.last_name].filter(Boolean).join(" ") || user.email;

  return (
    <div className="UsersTable">
      <Table>
        <Table.Header>
          {[
            <Table.HeaderCell key="member">Member</Table.HeaderCell>,
            <Table.HeaderCell key="email" width="16rem">
              Email
            </Table.HeaderCell>,
            <Table.HeaderCell key="role">Role</Table.HeaderCell>,
            <Table.HeaderCell key="status">Status</Table.HeaderCell>,
            <Table.HeaderCell key="cosigner" width="17rem">
              {`Co-signer (${approvedCount}/${maxCoSigners})`}
            </Table.HeaderCell>,
            ...(canManage ? [<Table.HeaderCell key="actions"> </Table.HeaderCell>] : []),
          ]}
        </Table.Header>

        <Table.Body>
          {users.map((u) => (
            <Table.BodyRow key={u.id}>
              {[
                <Table.BodyCell key="member">{renderMemberName(u)}</Table.BodyCell>,
                <Table.BodyCell key="email" width="16rem">
                  {u.email}
                </Table.BodyCell>,
                <Table.BodyCell key="role">{userRoleText(u.roles?.[0])}</Table.BodyCell>,
                <Table.BodyCell key="status">{u.is_active ? "Active" : "Inactive"}</Table.BodyCell>,
                <Table.BodyCell key="cosigner" width="17rem" allowOverflow className="CoSignerCell">
                  {renderCoSigner(u)}
                </Table.BodyCell>,
                ...(canManage
                  ? [
                      <Table.BodyCell key="actions">
                        <div className="FlexCellRight">
                          <DropdownMenu
                            triggerEl={
                              <MoreMenuButton aria-label={`Actions for ${displayName(u)}`} />
                            }
                          >
                            {renderChangeRoleMenuItems(u)}
                            <>{renderCoSignerMenuItems(u)}</>
                            {renderStatusMenuItem(u)}
                          </DropdownMenu>
                        </div>
                      </Table.BodyCell>,
                    ]
                  : []),
              ]}
            </Table.BodyRow>
          ))}
        </Table.Body>
      </Table>
    </div>
  );
};
